/** Mirrors backend / Prisma workflow types — extend when wiring APIs. */

export type Timestamp = Date | string;
export type Json = Record<string, unknown> | unknown[];
export type Nullable<T> = T | null;

export enum WL_ToolType {
  API = 'API',
  DATABASE = 'DATABASE',
}

export enum WL_ApiAuthType {
  HEADER = 'HEADER',
  BODY = 'BODY',
  QUERY = 'QUERY',
}

export enum WL_WorkflowNodeType {
  AGENT = 'AGENT',
  ORCHESTRATOR = 'ORCHESTRATOR',
}

export enum WL_ExpressionTypes {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  GREATER_THAN = 'GREATER_THAN',
  LESS_THAN = 'LESS_THAN',
  GREATER_THAN_OR_EQUALS = 'GREATER_THAN_OR_EQUALS',
  LESS_THAN_OR_EQUALS = 'LESS_THAN_OR_EQUALS',
  AND = 'AND',
  OR = 'OR',
}

/** Outgoing edge routing (forward only in UI; stored on edge until API merge). */
export type WorkflowEdgeTransitionType = 'LLM' | 'EXPRESSION' | 'ALWAYS';

export type {
  WorkflowConditionsResponseDto,
  WorkflowConnectorResponseDto,
  WorkflowConnectorSaveItemDto,
  WorkflowExpressionConditionResponseItemDto,
  WorkflowGraphResponseDto,
  WorkflowGraphSaveRequestDto,
  WorkflowLlmConditionResponseItemDto,
  WorkflowNodeResponseDto,
  WorkflowNodeSaveItemDto,
} from '../../../../../../core/models/chatbot-workflow-graph.model';

/** Persisted root fields for the Start node on the canvas. `pid` is null until returned from the API. */
export interface WorkflowStartMeta {
  pid?: number | null;
  chatbotId: number;
  name: string;
  systemPrompt: string | null;
}

export interface WLBotWorkflowNode {
  pid: number;
  chatbotId: number;
  nodeType: WL_WorkflowNodeType;
  name: string;
  systemPrompt?: string;
  knowledgeBaseId?: number;
  overrideSystemPrompt: boolean;
  modelId?: number;
  /** Client-only: drives model picker; not sent on save (API uses `modelId`). */
  providerId?: number | null;
  llmConditionId?: number;
  expressionId?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  deletedAt?: Timestamp;
}

/** Schedule configuration draft — lives on the canvas node until saved via the Schedule API. */
export interface NodeScheduleDraft {
  /** `null` for a schedule not yet persisted. */
  pid: number | null;
  name: string;
  triggerPrompt: string;
  cronExpr: string;
  timezone: string;
  isActive: boolean;
  maxRetries: number;
  retryIntervalMins: number;
  preflightCheckSql?: string | null;
  preflightMinRows?: number;
  /** nodeIds of other agents in this chatbot to pull context from. */
  upstreamWorkflowIds?: number[];
}

/** Client-only node kinds for the canvas (Start is not persisted as WLBotWorkflowNode). */
export type WorkflowCanvasNodeKind = 'START' | 'WORKFLOW';

/**
 * Draft payload for workflow nodes; Start nodes include this plus `startMeta` for UI.
 * `pid` is null until the DB assigns; `clientKey` is the canvas `id` for unpersisted nodes.
 */
export interface WorkflowNodeDraft extends Omit<Partial<WLBotWorkflowNode>, 'pid'> {
  pid?: number | null;
  clientKey?: string;
  isStartNode?: boolean;
  /** Mirrored from GET / save; omit on PUT to leave server tools unchanged. */
  toolIds?: number[];
  /** Client-only: schedule config for this node; synced to backend via ScheduleAgentService. */
  scheduleDraft?: NodeScheduleDraft | null;
}

export interface WorkflowCanvasNode {
  id: string;
  kind: WorkflowCanvasNodeKind;
  x: number;
  y: number;
  /** API root fields when `kind === 'START'`. */
  startMeta?: WorkflowStartMeta;
  workflow?: WorkflowNodeDraft;
}

export interface WorkflowCanvasEdge {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  /** Present when loaded from `WL_WorkflowNodeConnectors.pid`. */
  connectorPid?: number;
  /** Lower = evaluated first among edges from the same parent. */
  sortOrder?: number;
  /** Canvas label for this forward connection. */
  label?: string;
  transitionType?: WorkflowEdgeTransitionType;
  /** Draft copy for LLM routing. */
  llmConditionDraft?: string;
  expressionVariableDraft?: string;
  expressionOperatorDraft?: WL_ExpressionTypes;
  expressionValueDraft?: string;
  /**
   * Persisted `wl_bot:llm_conditions.pid` from GET `conditions.llmConditions[].conditionId`.
   * Sent as `conditionId` on save to upsert the row.
   */
  llmConditionRowId?: number;
  /**
   * Persisted `wl_bot:expressions.pid` from GET `conditions.expressionConditions[].conditionId`.
   * Sent as `conditionId` on save to upsert the row.
   */
  expressionConditionRowId?: number;
}
